import { Skeleton } from "@design";
import { useTranslation } from "react-i18next";
import { InlineNotice } from "@/components/InlineNotice";
import { Text } from "@/components/Text";
import { type KnowledgeImport, useImportErrorMessage } from "./useImports";

/** The share of parts read (semantic tokens only). */
function PartsBar({ done, total, label }: Readonly<{ done: number; total: number; label: string }>) {
  return (
    <progress
      aria-label={label}
      value={done}
      max={Math.max(total, 1)}
      className="h-2 w-full appearance-none overflow-hidden rounded-full bg-surface-inset [&::-moz-progress-bar]:bg-brand-primary [&::-webkit-progress-bar]:bg-surface-inset [&::-webkit-progress-value]:rounded-full [&::-webkit-progress-value]:bg-brand-primary"
    />
  );
}

function statusText(t: ReturnType<typeof useTranslation<"knowledge">>["t"], row: KnowledgeImport): string {
  const { partsDone, partsTotal } = row.progress;
  switch (row.status) {
    case "queued":
      return t("import.status.queued");
    case "extracting":
      return partsTotal > 0
        ? t("import.status.extractingParts", { filename: row.filename, done: partsDone, total: partsTotal })
        : t("import.status.extracting", { filename: row.filename });
    case "done":
      return row.pendingCount > 0 ? t("import.status.done", { count: row.pendingCount }) : t("import.status.doneNone");
    case "cancelled":
      return t("import.status.cancelled");
    default:
      return t("import.status.failed");
  }
}

/** Where an import is: reading parts, done (with caveats), or why it failed. */
export function ImportProgress({
  row,
  alreadyImported,
  loadFailed,
}: Readonly<{ row: KnowledgeImport | undefined; alreadyImported: boolean; loadFailed: boolean }>) {
  const { t } = useTranslation("knowledge");
  const message = useImportErrorMessage();
  if (loadFailed) {
    return (
      <Text role="alert" tone="error">
        {t("import.errors.KG_IMPORT_NOT_FOUND")}
      </Text>
    );
  }
  if (!row) return <Skeleton className="h-16 w-full" />;
  const running = row.status === "queued" || row.status === "extracting";
  return (
    <div className="flex flex-col gap-3" aria-live="polite">
      <Text>{statusText(t, row)}</Text>
      {running && (
        <PartsBar done={row.progress.partsDone} total={row.progress.partsTotal} label={t("import.progressLabel")} />
      )}
      {row.status === "failed" && (
        <Text role="alert" tone="error">
          {message(row.errorCode, t("import.errors.KG_IMPORT_FAILED"))}
        </Text>
      )}
      {alreadyImported && <InlineNotice tone="info">{t("import.alreadyImported")}</InlineNotice>}
      {row.status === "done" && row.progress.truncated && (
        <InlineNotice tone="warning">{t("import.truncated")}</InlineNotice>
      )}
      {row.status === "done" && row.progress.partsFailed > 0 && (
        <InlineNotice tone="warning">{t("import.partsFailed", { count: row.progress.partsFailed })}</InlineNotice>
      )}
    </div>
  );
}
